import { inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { ADDIN_CORE_CONFIG } from '../config/addin-core-config';
import { ConfigService } from '../config/config.service';
import { TenantService } from '../tenants/tenant.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _dialog: Office.Dialog | undefined;
  private readonly _router = inject(Router);
  private readonly _injector = inject(Injector);
  private readonly _configService = inject(ConfigService);
  private readonly _coreConfig = inject(ADDIN_CORE_CONFIG);
  private _userManager: UserManager | undefined;
  private _restorePromise: Promise<void> | undefined;
  private _refreshPromise: Promise<User | null> | undefined;

  public readonly user = signal<User | null>(null);

  private _getTenantService(): TenantService {
    return this._injector.get(TenantService);
  }

  private _getUserManager(sts?: string): UserManager {
    if (this._userManager) {
      return this._userManager;
    }

    const tenantSts = sts ?? this._getTenantService().selectedTenant()?.stsUrl;
    if (!tenantSts) {
      throw new Error('No tenant selected. Please select a tenant before accessing this feature.');
    }

    // Append STS to redirect URI if provided, so it's available in the callback
    let redirectUri = this._configService.config.oauth.redirectUri;
    if (sts) {
      const redirectUrl = new URL(redirectUri);
      redirectUrl.searchParams.set('sts', sts);
      redirectUri = redirectUrl.toString();
    }

    this._userManager = new UserManager({
      metadataUrl: `${tenantSts}/identity/.well-known/openid-configuration`,
      scope: this._configService.config.oauth.scopes,
      authority: tenantSts,
      client_id: this._configService.config.oauth.clientId,
      redirect_uri: redirectUri,
      userStore: new WebStorageStateStore({ store: window.localStorage })
    });

    this._userManager.events.addUserLoaded((loadedUser) => {
      this.user.set(loadedUser);
    });

    // Handle token refresh events
    this._userManager.events.addAccessTokenExpiring(() => {
      // Token is about to expire, proactively refresh it
      void this.refreshToken();
    });

    this._userManager.events.addAccessTokenExpired(() => {
      // Token expired, try to refresh it
      void this.refreshToken();
    });

    return this._userManager;
  }

  public async restoreUser(): Promise<void> {
    try {
      const storedUser = await this._getUserManager().getUser();
      if (storedUser) {
        this.user.set(storedUser);
      }
    } catch (error) {
      console.error('Failed to restore user from storage:', error);
      // Clear any potentially corrupted stored data
      await this._getUserManager().removeUser();
    }
  }

  public async ensureUserRestored(): Promise<void> {
    if (!this._restorePromise) {
      this._restorePromise = this.restoreUser();
    }
    await this._restorePromise;
  }

  public async refreshToken(): Promise<User | null> {
    // Prevent concurrent refresh attempts
    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    if (!this._userManager) {
      return null;
    }

    this._refreshPromise = (async (): Promise<User | null> => {
      try {
        const refreshedUser = await this._userManager!.signinSilent();
        if (refreshedUser) {
          this.user.set(refreshedUser);
          return refreshedUser;
        }
        return null;
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Refresh failed, clear user
        this.user.set(null);
        return null;
      } finally {
        // Clear the promise so future refresh attempts can proceed
        this._refreshPromise = undefined;
      }
    })();

    return this._refreshPromise;
  }

  public async revokeTokens(): Promise<void> {
    if (this._userManager) {
      try {
        await this._userManager.revokeTokens();
      } catch (error) {
        console.error('Failed to revoke tokens:', error);
      }
    }
  }

  public async clearAuth(): Promise<void> {
    if (this._userManager) {
      try {
        await this._userManager.removeUser();
      } catch (error) {
        console.error('Failed to remove user from storage:', error);
      }
    }
    this._userManager = undefined;
    this.user.set(null);
    this._restorePromise = undefined;
    this._refreshPromise = undefined;
  }

  public async signIn(sts?: string): Promise<void> {
    await this._getUserManager(sts).signinRedirect();
  }

  public async signinResponse(sts?: string): Promise<void> {
    await this._getUserManager(sts).signinRedirectCallback();
  }

  public openDialog(): void {
    const messageHandler = (
      args: { message: string; origin: string | undefined } | { error: number }
    ): void => {
      if ('message' in args) {
        const user = User.fromStorageString(args.message);
        void this._getUserManager()
          .storeUser(user)
          .then(() => {
            this._dialog?.close();
            this.user.set(user);
            void this._router.navigate([this._coreConfig.homeRoute]);
          });
      } else if ('error' in args) {
        console.error(`Dialog error: ${args.error}`);
      }
    };

    const tenant = this._getTenantService().selectedTenant();
    if (!tenant) {
      throw new Error('No tenant selected. Please select a tenant before accessing this feature.');
    }

    const dialogUrl = new URL(`${window.location.origin}/oauth/login`);
    dialogUrl.searchParams.set('sts', tenant.stsUrl);

    Office.context.ui.displayDialogAsync(
      dialogUrl.toString(),
      { height: 50, width: 50, promptBeforeOpen: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          this._dialog = result.value;
          this._dialog.addEventHandler(Office.EventType.DialogMessageReceived, messageHandler);
        } else {
          console.log(result.error);
        }
      }
    );
  }
}
